import { type GQLError } from '@egw/clients'
import { useQuery, useQueryClient, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query'
import {
  type InvestmentFixedIncomeCategoriesRecommendationOutput,
  type InvestmentAccountActivity,
  type InvestmentAccountActivityInput,
  type InvestmentAccountDetail,
  type InvestmentAssetAllocationRecommendationOutput,
  type InvestmentCashEquivalentCategoriesRecommendationOutput,
  type InvestmentEquitiesSectorsRecommendationOutput,
  type InvestmentInstrument,
  type InvestmentTradeDetailInput,
  type InvestmentTradeDetailOutput,
  type InvestmentTradeConfirmsInput,
  type InvestmentTradeConfirmsOutput,
  type InvestmentsTradeDetailInput,
  type InvestmentAssetAllocation,
} from '../../models'
import {
  getCashEquivalentCategoriesRecommendation,
  getInvestmentAccountActivity,
  getInvestmentAccountDetail,
  getInvestmentInstrument,
  getInvestmentRiskToleranceRecommendation,
  getInvestmentTradeDetail,
  getInvestmentTradeConfirms,
  getPortfolioOverviewAssetAllocation,
} from '../../services'
import { getInvestmentEquitiesRecommendation } from '../../services/investments/equities/equities'
import { getInvestmentFixedIncomeRecommendation } from '../../services/investments/fixed-income/fixed-income'

/**
 * Investments Account Detail state hook
 * @param alias - investment account alias
 * @param options - tanstack useQuery options
 * @returns tanstack useQuery result, data is of type InvestmentAccountDetail
 */
export const useInvestmentAccountDetail = (
  alias: number,
  options?: Omit<UseQueryOptions<InvestmentAccountDetail, GQLError>, 'queryKey' | 'queryFn' | 'select'>,
): UseQueryResult<InvestmentAccountDetail, GQLError> =>
  useQuery<InvestmentAccountDetail, GQLError>({
    queryKey: ['getInvestmentAccountDetail', alias],
    queryFn: () => getInvestmentAccountDetail(alias),
    ...options,
  })

/**
 * Investments Account Detail state hook
 * @param alias - investment account alias
 * @param options - tanstack useQuery options
 * @returns tanstack useQuery result, data is of type InvestmentAccountDetail
 */
export const useInvestmentInstrumentDetail = (
  symbol: string,
  options?: Omit<UseQueryOptions<InvestmentInstrument, GQLError>, 'queryKey' | 'queryFn' | 'select'>,
): UseQueryResult<InvestmentInstrument, GQLError> =>
  useQuery<InvestmentInstrument, GQLError>({
    queryKey: ['getInvestmentInstrument', symbol],
    queryFn: () => getInvestmentInstrument(symbol),
    ...options,
  })

/**
 * Get Investment Account Activity
 * @param investmentAccountActivityInput - alias: number
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<InvestmentAccountActivity>
 * @example const InvestmentAccountActivity = await getInvestmentAccountActivity();
 */
export const useInvestmentAccountActivity = (
  investmentAccountActivityInput: InvestmentAccountActivityInput,
  options?: Omit<UseQueryOptions<InvestmentAccountActivity, GQLError>, 'queryKey' | 'queryFn' | 'select'>,
): UseQueryResult<InvestmentAccountActivity, GQLError> => {
  return useQuery<InvestmentAccountActivity, GQLError>({
    queryKey: ['getInvestmentAccountActivity', { alias: investmentAccountActivityInput.alias }],
    queryFn: () => getInvestmentAccountActivity(investmentAccountActivityInput),
    ...options,
  })
}

/**
 * Get Investment Account Detail
 * @param investmentTradeDetailInput - tradeId: number
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<InvestmentTradeDetailOutput>
 * @example const investmentTradeDetail = await getInvestmentTradeDetail(\{ tradeId: number \});
 */
export const useInvestmentTradeDetail = (
  investmentTradeDetailInput: InvestmentTradeDetailInput,
  options?: Omit<UseQueryOptions<InvestmentTradeDetailOutput, GQLError>, 'queryKey' | 'queryFn' | 'select'>,
): UseQueryResult<InvestmentTradeDetailOutput, GQLError> =>
  useQuery<InvestmentTradeDetailOutput, GQLError>({
    queryKey: ['getInvestmentTradeDetail', investmentTradeDetailInput.tradeId],
    queryFn: () => getInvestmentTradeDetail(investmentTradeDetailInput),
    ...options,
  })

/**
 * Get Investment Account Detail
 * @param investmentsTradeDetailInput - tradeId: number ; alias: number
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<InvestmentTradeDetailOutput>
 * @example const investmentTradeDetail = await getInvestmentTradeDetail(\{ tradeId: number \});
 */
// TO BE USED FOR INVESTMENTS (IRAS) TRADE DETAIL QUERY
// THIS QUERY INVALIDATES THE INVESTMENT ACTIVITY QUERY TO KEEP DATA IN SYNC
export const useInvestmentsTradeDetail = (
  investmentsTradeDetailInput: InvestmentsTradeDetailInput,
  options?: Omit<UseQueryOptions<InvestmentTradeDetailOutput, GQLError>, 'queryKey' | 'queryFn' | 'select'>,
): UseQueryResult<InvestmentTradeDetailOutput, GQLError> => {
  const client = useQueryClient()

  return useQuery<InvestmentTradeDetailOutput, GQLError>({
    queryKey: ['getInvestmentTradeDetail', investmentsTradeDetailInput.tradeId],
    queryFn: async () => {
      // INVALIDATE THE QUERY THAT ORIGINALLY FETCHED THE ACCOUNT ACTIVITY
      // HELPS KEEP DATA IN SYNC BETWEEN QUERIES, ESPECIALLY SINCE TRADES MAY COMPLETE DURING INITIAL ACTIVITY QUERY AND SUBSEQUENT TRADE DETAIL QUERY
      void client.invalidateQueries({
        queryKey: ['getInvestmentAccountActivity', { alias: investmentsTradeDetailInput.alias }],
        refetchType: 'all',
      })

      return await getInvestmentTradeDetail({
        tradeId: investmentsTradeDetailInput.tradeId,
      } as unknown as InvestmentTradeDetailInput)
    },
    ...options,
  })
}

/**
 * Get investment risk tolerance recommendation
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<InvestmentAssetAllocationRecommendationOutput>
 * @example const investmentRiskToleranceRecommendation = await useInvestmentRiskToleranceRecommendation();
 */
export const useInvestmentRiskToleranceRecommendation = (
  options?: Omit<
    UseQueryOptions<InvestmentAssetAllocationRecommendationOutput, GQLError>,
    'queryKey' | 'queryFn' | 'select'
  >,
): UseQueryResult<InvestmentAssetAllocationRecommendationOutput, GQLError> =>
  useQuery<InvestmentAssetAllocationRecommendationOutput, GQLError>({
    queryKey: ['getInvestmentRiskToleranceRecommendation'],
    queryFn: () => getInvestmentRiskToleranceRecommendation(),
    ...options,
  })

/**
 * Get Cash Equivalent Categories Recommendation
 * @param options - tanstack useQuery options *
 * @returns Promise<InvestmentCashEquivalentCategoriesRecommendationOutput>
 */
export const useCashEquivalentCategoriesRecommendation = (
  options?: Omit<
    UseQueryOptions<InvestmentCashEquivalentCategoriesRecommendationOutput, GQLError>,
    'queryKey' | 'queryFn' | 'select'
  >,
): UseQueryResult<InvestmentCashEquivalentCategoriesRecommendationOutput, GQLError> =>
  useQuery<InvestmentCashEquivalentCategoriesRecommendationOutput, GQLError>({
    queryKey: ['investmentCashEquivalentCategoriesRecommendation'],
    queryFn: () => getCashEquivalentCategoriesRecommendation(),
    ...options,
  })

/**
 * Get Equities Recommendation
 * @param options - tanstack useQuery options *
 * @returns Promise<InvestmentEquitiesSectorsRecommendationOutput>
 */
export const useEquitiesSectorRecommendation = (
  options?: Omit<
    UseQueryOptions<InvestmentEquitiesSectorsRecommendationOutput, GQLError>,
    'queryKey' | 'queryFn' | 'select'
  >,
): UseQueryResult<InvestmentEquitiesSectorsRecommendationOutput, GQLError> =>
  useQuery<InvestmentEquitiesSectorsRecommendationOutput, GQLError>({
    queryKey: ['investmentEquitiesSectorsRecommendation'],
    queryFn: () => getInvestmentEquitiesRecommendation(),
    ...options,
  })

/**
 * Get Fixed Income Categories Recommendation
 * @param options - tanstack useQuery options *
 * @returns Promise<InvestmentFixedIncomeCategoriesRecommendationOutput>
 */
export const useFixedIncomeCategoriesRecommedation = (
  options?: Omit<
    UseQueryOptions<InvestmentFixedIncomeCategoriesRecommendationOutput, GQLError>,
    'queryKey' | 'queryFn' | 'select'
  >,
): UseQueryResult<InvestmentFixedIncomeCategoriesRecommendationOutput, GQLError> =>
  useQuery<InvestmentFixedIncomeCategoriesRecommendationOutput, GQLError>({
    queryKey: ['investmentFixedIncomeCategoriesRecommendation'],
    queryFn: () => getInvestmentFixedIncomeRecommendation(),
    ...options,
  })

/**
 * investmentTradeConfirms state hook
 * @param options - tanstack useQuery options
 * @returns tanstack useQuery result, data is of type InvestmentTradeConfirms
 */
export const useInvestmentTradeConfirms = (
  investmentTradeConfirmsInput: InvestmentTradeConfirmsInput,
  options?: Omit<UseQueryOptions<InvestmentTradeConfirmsOutput, GQLError>, 'queryKey' | 'queryFn' | 'select'>,
): UseQueryResult<InvestmentTradeConfirmsOutput, GQLError> =>
  useQuery<InvestmentTradeConfirmsOutput, GQLError>({
    queryKey: ['investmentTradeConfirms', investmentTradeConfirmsInput.alias],
    queryFn: () => getInvestmentTradeConfirms(investmentTradeConfirmsInput),
    ...options,
  })

export const usePortfolioOverviewAssetAllocation = (
  options?: Omit<UseQueryOptions<InvestmentAssetAllocation | undefined, GQLError>, 'queryKey' | 'queryFn' | 'select'>,
): UseQueryResult<InvestmentAssetAllocation | undefined, GQLError> =>
  useQuery<InvestmentAssetAllocation | undefined, GQLError>({
    queryKey: ['portfolioOverviewAssetAllocation'],
    queryFn: () => getPortfolioOverviewAssetAllocation(),
    ...options,
  })
